import { CHANNELS } from "./channels";

// what each person may see and do. fractal (the owner) always has everything, everyone else gets a level per
// feature from the people dashboard. shared by the server (which enforces it) and the app (which hides what's off)

export type Level = "none" | "read" | "edit";

export interface Feature {
  id: string;
  label: string;
  detail: string;
  // the levels this feature can be set to. per person features are just off or on
  levels: Level[];
  // places in the app this feature opens
  places: string[];
  // a feature added later starts at the level someone already has for this one, until it's set on the dashboard
  from?: string;
}

export const FEATURES: Feature[] = [
  { id: "chi", label: "Talk to Chi", detail: "their own chat and memory, on your claude account", levels: ["none", "edit"], places: ["chat"] },
  { id: "refs", label: "References", detail: "the shared ref library", levels: ["none", "read", "edit"], places: ["references"] },
  { id: "ocs", label: "Characters", detail: "the shared oc sheets", levels: ["none", "read", "edit"], places: ["ocs"] },
  { id: "notes", label: "Notes", detail: "their own", levels: ["none", "edit"], places: ["notes"] },
  { id: "time", label: "Calendar and pings", detail: "their own", levels: ["none", "edit"], places: ["when"] },
  { id: "routines", label: "Routines", detail: "their own", levels: ["none", "edit"], places: ["routines"] },
  { id: "goals", label: "Goals", detail: "their own sticky goals with progress", levels: ["none", "edit"], places: ["goals"], from: "routines" },
  { id: "tasks", label: "Tasks", detail: "plane and chix bot, their own", levels: ["none", "edit"], places: ["tasks"] },
  { id: "studio", label: "Studio", detail: "timer, palette, prompts, comms and their money stats", levels: ["none", "edit"], places: ["timer", "palette", "prompts", "jobs"] },
  { id: "budget", label: "Budget", detail: "their own bills, costs, debts and goals", levels: ["none", "edit"], places: ["budget"], from: "notes" },
  { id: "maps", label: "Maps", detail: "places nearby, routes and their saved spots, on your google maps key", levels: ["none", "edit"], places: ["maps"] },
  { id: "practice", label: "Practice", detail: "gesture drawing and japanese", levels: ["none", "edit"], places: ["gesture", "japanese"] },
  { id: "doodles", label: "Doodles", detail: "the shared doodle wall", levels: ["none", "read", "edit"], places: ["doodles"], from: "ocs" },
  { id: "tierlists", label: "Tier lists", detail: "their own, and the links they share", levels: ["none", "edit"], places: ["tierlists"], from: "doodles" },
  { id: "music", label: "Music", detail: "their own songs and playlists, from uploads and youtube links", levels: ["none", "edit"], places: ["music"], from: "doodles" },
  { id: "tracker", label: "Tracker", detail: "their own snes game songs, exported as impulse tracker files", levels: ["none", "edit"], places: ["tracker"], from: "music" },
  { id: "whiteboard", label: "Whiteboards", detail: "their own boards, and your shared ones (read looks, edit draws)", levels: ["none", "read", "edit"], places: ["whiteboard"], from: "doodles" },
  { id: "awards", label: "Achievements", detail: "points, medals and the leaderboard, their own score shown to everyone with this on", levels: ["none", "edit"], places: ["achievements"], from: "notes" },
  { id: "board", label: "Feed", detail: "the shared feed: posts, comments, medals and what happens between people, shown on home", levels: ["none", "read", "edit"], places: ["feed"], from: "chi" },
  { id: "scans", label: "Scans", detail: "their own scan library", levels: ["none", "edit"], places: ["scan"] },
  { id: "speaker", label: "Chi speaker", detail: "their pings ring at home, chi answers their voice", levels: ["none", "edit"], places: [] },
  { id: "dms", label: "Discord messages", detail: "chi can dm your contacts for them", levels: ["none", "edit"], places: [] }
];

// never shared, listed on the dashboard so it's clear they stay yours
export const OWNER_ONLY = [
  { label: "Server", detail: "vps panel and bot controls" },
  { label: "Logs", detail: "every log line" },
  { label: "Claude usage", detail: "spend and billing" },
  { label: "Keys and server setup", detail: "api keys, ssh" },
  { label: "People", detail: "this dashboard" }
];
export const OWNER_PLACES = ["box", "logs", "claude", "people"];

export type Access = Record<string, Level>;

// pauline's access from before the dashboard, so nothing changes for her until it's edited
export const FULL_PRESET: Access = Object.fromEntries(FEATURES.map((feature) => [feature.id, feature.levels[feature.levels.length - 1]]));

// someone new can talk to chi and keep their own notes, pings, routines and goals. the rest waits for a toggle
export const STARTER_PRESET: Access = {
  ...Object.fromEntries(FEATURES.map((feature) => [feature.id, "none" as Level])),
  chi: "edit",
  notes: "edit",
  time: "edit",
  routines: "edit",
  goals: "edit",
  board: "edit",
  awards: "edit"
};

const RANK: Record<Level, number> = { none: 0, read: 1, edit: 2 };

export function atLeast(have: Level | undefined, want: Level): boolean {
  return RANK[have || "none"] >= RANK[want];
}

// keeps only known features at levels they allow
export function cleanAccess(raw: unknown): Access {
  const given = raw && typeof raw === "object" ? raw as Record<string, unknown> : {};
  return Object.fromEntries(FEATURES.map((feature) => {
    const key = given[feature.id] === undefined && feature.from ? feature.from : feature.id;
    const level = String(given[key] || "none") as Level;
    return [feature.id, feature.levels.includes(level) ? level : "none"];
  }));
}

export function placeAllowed(placeId: string, access: Access | null): boolean {
  if (!access) return true;
  if (OWNER_PLACES.includes(placeId)) return false;
  const feature = FEATURES.find((item) => item.places.includes(placeId));
  return !feature || atLeast(access[feature.id], "read");
}

// what every rpc channel needs. "anyone" is fine for any login, "owner" is fractal only.
// a channel missing from here is owner only, so a new feature is never open by accident
type Rule = "anyone" | "owner" | [string, Level];

const C = CHANNELS;
const RULES: Record<string, Rule> = {
  [C.settingsGet]: "anyone",
  [C.settingsSave]: "anyone",
  [C.navOrderSave]: "anyone",
  [C.journalAdd]: "anyone",
  [C.statsList]: "anyone",
  [C.tabsShow]: "anyone",
  [C.tabsHide]: "anyone",
  [C.tabsReload]: "anyone",
  [C.tabsHome]: "anyone",
  [C.openExternal]: "anyone",

  [C.assistantSend]: ["chi", "edit"],
  [C.assistantReset]: ["chi", "edit"],
  [C.assistantOpenMemory]: ["chi", "edit"],

  // the extension saves refs, so its token comes with them
  [C.extensionTokenGet]: ["refs", "edit"],
  [C.extensionTokenReset]: ["refs", "edit"],

  [C.referencesList]: ["refs", "read"],
  [C.referencesTag]: ["refs", "edit"],
  [C.referencesCaption]: ["refs", "edit"],
  [C.referencesRemove]: ["refs", "edit"],
  [C.referencesRetry]: ["refs", "edit"],
  [C.referencesImport]: ["refs", "edit"],
  [C.referencesIndex]: ["refs", "edit"],
  [C.referencesIndexOne]: ["refs", "edit"],
  [C.referencesReindex]: ["refs", "edit"],

  [C.ocsList]: ["ocs", "read"],
  [C.ocsSave]: ["ocs", "edit"],
  [C.ocsRemove]: ["ocs", "edit"],
  [C.ocsImportZirk]: ["ocs", "edit"],
  [C.ocsImportDiscord]: ["ocs", "edit"],
  [C.ocsMerge]: ["ocs", "edit"],
  [C.ocsRemovePicture]: ["ocs", "edit"],
  [C.ocsSetPicture]: ["ocs", "edit"],
  // taking or leaving ownership only touches your own name on the sheet
  [C.ocsOwn]: ["ocs", "read"],
  [C.ocsIndex]: ["ocs", "edit"],
  [C.ocsIndexStatus]: ["ocs", "read"],

  // sharing is between people, sharing_service checks who shared what with whom
  [C.sharesGet]: "anyone",
  [C.sharesSet]: "anyone",
  [C.sharedRead]: "anyone",

  [C.notesList]: ["notes", "edit"],
  [C.notesSave]: ["notes", "edit"],
  [C.notesRemove]: ["notes", "edit"],
  [C.notesAppendInbox]: ["notes", "edit"],

  [C.calendarList]: ["time", "edit"],
  [C.calendarAdd]: ["time", "edit"],
  [C.calendarRemove]: ["time", "edit"],
  [C.calendarLinksList]: ["time", "edit"],
  [C.calendarLinksAdd]: ["time", "edit"],
  [C.calendarLinksRemove]: ["time", "edit"],
  [C.calendarLinksSync]: ["time", "edit"],
  [C.calendarLinksSetVisible]: ["time", "edit"],
  [C.remindersList]: ["time", "edit"],
  [C.remindersAdd]: ["time", "edit"],
  [C.remindersRemove]: ["time", "edit"],

  [C.routinesList]: ["routines", "edit"],
  [C.routinesSave]: ["routines", "edit"],
  [C.routinesRemove]: ["routines", "edit"],
  [C.routinesDone]: ["routines", "edit"],

  [C.goalsList]: ["goals", "edit"],
  [C.goalsSave]: ["goals", "edit"],
  [C.goalsStep]: ["goals", "edit"],
  [C.goalsProgress]: ["goals", "edit"],
  [C.goalsDone]: ["goals", "edit"],
  [C.goalsRemove]: ["goals", "edit"],

  [C.tasksList]: ["tasks", "edit"],
  [C.tasksBoards]: ["tasks", "edit"],
  [C.tasksStatus]: ["tasks", "edit"],
  [C.tasksHide]: ["tasks", "edit"],
  [C.tasksView]: ["tasks", "edit"],
  [C.tasksPick]: ["tasks", "edit"],
  [C.tasksBoardAdd]: ["tasks", "edit"],
  [C.tasksBoardRename]: ["tasks", "edit"],
  [C.tasksBoardRemove]: ["tasks", "edit"],
  [C.tasksPlace]: ["tasks", "edit"],
  [C.chixAdd]: ["tasks", "edit"],
  [C.chixEdit]: ["tasks", "edit"],
  [C.chixComplete]: ["tasks", "edit"],
  [C.planeConnect]: ["tasks", "edit"],

  [C.studioGet]: ["studio", "edit"],
  [C.studioAddColor]: ["studio", "edit"],
  [C.studioRemoveColor]: ["studio", "edit"],
  [C.studioAddPrompt]: ["studio", "edit"],
  [C.studioRemovePrompt]: ["studio", "edit"],
  [C.studioAddJob]: ["studio", "edit"],
  [C.studioRemoveJob]: ["studio", "edit"],
  [C.studioView]: ["studio", "edit"],
  [C.studioSaveType]: ["studio", "edit"],
  [C.studioRemoveType]: ["studio", "edit"],
  [C.studioStarterTypes]: ["studio", "edit"],
  [C.studioAddPayment]: ["studio", "edit"],
  [C.studioRemovePayment]: ["studio", "edit"],
  [C.studioAddCredit]: ["studio", "edit"],
  [C.studioRemoveCredit]: ["studio", "edit"],
  [C.studioSaveClient]: ["studio", "edit"],
  [C.studioSaveClientOc]: ["studio", "edit"],
  [C.studioRemoveClientOc]: ["studio", "edit"],
  [C.studioSuggestRefs]: ["refs", "read"],
  [C.studioDescribeOc]: ["studio", "edit"],

  [C.budgetGet]: ["budget", "edit"],
  [C.budgetSave]: ["budget", "edit"],
  [C.budgetRemove]: ["budget", "edit"],
  [C.budgetPay]: ["budget", "edit"],
  [C.budgetStop]: ["budget", "edit"],
  // adding to someone else's budget, sharing_service checks they let you
  [C.budgetSharedSave]: "anyone",
  [C.budgetSharedRemove]: "anyone",
  [C.budgetSharedPay]: "anyone",
  [C.budgetSharedStop]: "anyone",

  [C.mapsConfig]: ["maps", "edit"],
  [C.mapsNearby]: ["maps", "edit"],
  [C.mapsRoute]: ["maps", "edit"],
  [C.mapsGet]: ["maps", "edit"],
  [C.mapsSavePlace]: ["maps", "edit"],
  [C.mapsRemovePlace]: ["maps", "edit"],
  [C.mapsSaveRoute]: ["maps", "edit"],
  [C.mapsRemoveRoute]: ["maps", "edit"],

  // profiles and alters, alters_service checks who owns what
  [C.profileGet]: "anyone",
  [C.profileSave]: "anyone",
  [C.profileAvatar]: "anyone",
  [C.altersCreate]: "anyone",
  [C.altersRemove]: "anyone",
  [C.altersOwner]: "anyone",

  // everyone's own wheels, like the calculator it isn't a feature to switch on
  [C.rouletteGet]: "anyone",
  [C.rouletteSave]: "anyone",
  [C.rouletteRemove]: "anyone",
  [C.rouletteSpun]: "anyone",

  [C.gesturePick]: ["practice", "edit"],
  [C.japaneseToday]: ["practice", "edit"],
  [C.japaneseAnswer]: ["practice", "edit"],
  [C.japaneseSettings]: ["practice", "edit"],

  [C.printersList]: ["scans", "edit"],
  [C.scansList]: ["scans", "edit"],
  [C.scansInbox]: ["scans", "edit"],
  [C.scansCapture]: ["scans", "edit"],
  [C.scansImport]: ["scans", "edit"],
  [C.scansRead]: ["scans", "edit"],
  [C.scansSave]: ["scans", "edit"],
  [C.scansRemove]: ["scans", "edit"],

  [C.doodlesList]: ["doodles", "read"],
  [C.doodlesAdd]: ["doodles", "edit"],
  [C.doodlesSave]: ["doodles", "edit"],
  [C.doodlesRemove]: ["doodles", "edit"],
  // everyone who sees the wall keeps their own tally
  [C.doodlesCount]: ["doodles", "read"],

  [C.tierlistsList]: ["tierlists", "edit"],
  [C.tierlistsGet]: ["tierlists", "edit"],
  [C.tierlistsCreate]: ["tierlists", "edit"],
  [C.tierlistsSave]: ["tierlists", "edit"],
  [C.tierlistsRemove]: ["tierlists", "edit"],
  [C.tierlistsImages]: ["tierlists", "edit"],
  [C.tierlistsShare]: ["tierlists", "edit"],

  [C.trackerList]: ["tracker", "edit"],
  [C.trackerGet]: ["tracker", "edit"],
  [C.trackerCreate]: ["tracker", "edit"],
  [C.trackerSave]: ["tracker", "edit"],
  [C.trackerRemove]: ["tracker", "edit"],

  [C.musicLibrary]: ["music", "edit"],
  [C.musicUpload]: ["music", "edit"],
  [C.musicYoutube]: ["music", "edit"],
  [C.musicJobs]: ["music", "edit"],
  [C.musicTools]: ["music", "edit"],
  [C.musicSaveTrack]: ["music", "edit"],
  [C.musicRemoveTrack]: ["music", "edit"],
  [C.musicSavePlaylist]: ["music", "edit"],
  [C.musicRemovePlaylist]: ["music", "edit"],

  // private boards are always the person's own, boards_service checks who may draw on shared ones
  [C.boardsList]: ["whiteboard", "read"],
  [C.boardsGet]: ["whiteboard", "read"],
  [C.boardsCreate]: ["whiteboard", "read"],
  [C.boardsApply]: ["whiteboard", "read"],
  [C.boardsThumb]: ["whiteboard", "read"],
  [C.boardsPresence]: ["whiteboard", "read"],
  [C.boardsRemove]: ["whiteboard", "read"],
  [C.awardsGet]: ["awards", "edit"],
  [C.awardsBoard]: ["awards", "edit"],
  [C.awardsClean]: ["awards", "edit"],
  [C.awardsEvent]: ["awards", "edit"],
  [C.awardsVisible]: ["awards", "edit"],
  // the daily drawing challenge is for everyone, the suggested refs only come from the ref library when someone can see it
  [C.feedList]: ["board", "read"],
  [C.feedCheer]: ["board", "edit"],
  [C.feedPost]: ["board", "edit"],
  // feed_service lets people change only their own posts and comments
  [C.feedEdit]: ["board", "edit"],
  [C.feedComment]: ["board", "edit"],
  [C.feedCommentRemove]: ["board", "edit"],
  // feed_service lets people remove their own posts, fractal anyone's
  [C.feedRemove]: ["board", "edit"],
  [C.challengeGet]: "anyone",
  [C.challengeAct]: "anyone",
  // suggestions are open to everyone who signs in, it is how people tell fractal what to fix
  [C.suggestionsList]: "anyone",
  [C.suggestionsPost]: "anyone",
  [C.suggestionsVote]: "anyone",
  // only fractal sets a status, suggestions_service also lets people remove their own
  [C.suggestionsStatus]: "owner",
  [C.suggestionsRemove]: "anyone",
  // the daily question is for everyone who signs in
  [C.pollGet]: "anyone",
  [C.pollVote]: "anyone",
  [C.boardList]: ["board", "read"],
  [C.boardPost]: ["board", "edit"],
  // board_service lets people change only their own lines, fractal can clear anyone's
  [C.boardEdit]: ["board", "edit"],
  [C.boardRemove]: ["board", "edit"],

  // just tips and minipis, nothing personal
  [C.minipiMotd]: "anyone"
};

// the feature a channel belongs to, for counting which parts of chix someone has tried
export function featureOfChannel(channel: string): string {
  const rule = RULES[channel];
  return Array.isArray(rule) ? rule[0] : "";
}

export function channelAllowed(channel: string, owner: boolean, access: Access): boolean {
  if (owner) return true;
  const rule = RULES[channel] || "owner";
  if (rule === "anyone") return true;
  if (rule === "owner") return false;
  return atLeast(access[rule[0]], rule[1]);
}
